<template>
  <div class="list-container">
    <h1 class="page-title">{{ building.building_nm }} {{ year }}年 年間予定表</h1>

    <div class="mb-2 d-flex justify-content-between">
      <div>
        <router-link :to="{ name: 'AnnualWorkScheduleCreate', params: { building_id: buildingId, start_month: startMonth }, query: route.query }">
          <button type="button" class="btn btn-sm btn-primary">業務内容追加</button>
        </router-link>

        <button
          class="btn btn-sm btn-success ms-2"
          :class="{ disabled: !isSortUpdated }"
          :disabled="!isSortUpdated"
          @click="saveSortOrder"
        >
          並び順を保存
        </button>
      </div>

      <div>
        <!-- 昨年度ボタン -->
        <router-link
          v-if="prevFiscalValue"
          :key="prevFiscalValue.start"
          :to="{ name: 'AnnualWorkScheduleList', params: { building_id: buildingId.value, start_month: prevFiscalValue.start }, query: route.query }"
        >
          <button type="button" class="btn btn-sm btn-success mx-1 my-1">
            {{ prevFiscalValue.label }}
          </button>
        </router-link>

        <!-- 来年度ボタン -->
        <router-link
          v-if="nextFiscalValue"
          :key="nextFiscalValue.start"
          :to="{ name: 'AnnualWorkScheduleList', params: { building_id: buildingId.value, start_month: nextFiscalValue.start }, query: route.query }"
        >
          <button type="button" class="btn btn-sm btn-info mx-1 my-1">
            {{ nextFiscalValue.label }}
          </button>
        </router-link>
      </div>

      <div>
        <!--
        <router-link :to="{ name: 'CustomerBuildingList'}">
           <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        </router-link>
        -->

        <router-link :to="{ name: 'CustomerBuildingList', params: { id: building.id }, query: route.query }"> <!-- 顧客情報編集ページへのリンク -->
          <button type="button" class="btn btn-sm btn-secondary me-2">年間作業予定の建物一覧へ</button>
        </router-link>

        <router-link :to="{ name: 'BuildingList', params: { id: building.id }, query: route.query }"> <!-- 顧客情報編集ページへのリンク -->
          <button type="button" class="btn btn-sm btn-primary">建物マスタ情報へ</button>
        </router-link>
      </div>
    </div>

    <div class="warning-comment">
      <p class="mb-0">・作業予定に「◯」がついている場合は、背景色が黄緑になっています。</p>
      <p class="mb-1">・建物マスタの定額請求が「対象」となっている場合は、定額請求の表示レイアウトになります。</p>
    </div>
    
    <div class="table-wrapper">
      <table class="table table-bordered table-hover">
        <thead>
          <tr>
            <th class="text-nowrap bg-tb-header th-sticky sticky-left">業務内容</th>
            <th class="text-nowrap bg-tb-header th-sticky sticky-left-second">業務単価</th>
            <th class="text-nowrap bg-tb-header th-sticky sticky-left-third">協力会社</th>
            <th class="text-nowrap bg-tb-header th-sticky sticky-left-forth">操作</th>
            <th class="text-nowrap bg-tb-header" v-for="monthOffset in 12" :key="monthOffset">
              <div>{{ displayMonth(monthOffset) }}</div>
            </th>
            <th class="text-nowrap bg-tb-header">計</th>
          </tr>
        </thead>

        <!-- ######### 定額請求の場合（差し込み）######### -->
        <tr v-if="building.fixed_fee_billing" class="no-drag-drop-element">
          <td colspan="4" class="bg-tb-side-header sticky-left bg-color-fixed-fee">定額請求</td>
          <td
            class="text-nowrap text-end"
            v-for="(total, index) in monthlyBillTotals"
            :key="index"
            :class="{
              // isBeforeContractEndを使って背景色を切り替え（わかりやすく簡潔に）
              'bg-light-gray': isBeforeContractEnd(baseDates[index]),
              'bg-color-fixed-fee': !isBeforeContractEnd(baseDates[index])
            }"
          >
            {{ formatCurrencyZero(total) }}
          </td>
          <td class="text-nowrap text-end bg-color-fixed-fee">{{ formatCurrencyZero(annualTotalAmount) }}</td>
        </tr>
        <!-- ######### 定額請求の場合（差し込み）######### -->

        <!-- ドラッグ可能なtbody -->
        <Draggable
          tag="tbody"
          v-model="schedules"
          handle=".drag-handle"
          @end="onReorderEnd"
        >

          <!-- 👇 これを追加（中身は空でOK） -->
          <template #header></template>

          <template #item="{ element: schedule }">
            <tr :key="schedule.id" class="schedule-data-area">
              <!-- ドラッグハンドルを追加 -->
              <td class="bg-tb-side-header sticky-left drag-handle">
                {{ schedule.work_content }}
                <span
                  v-if="schedule.execution_months_pending"
                  class="badge text-bg-warning ms-2"
                >
                  実施月未設定
                </span>
                <img
                  v-if="schedule.remarks"
                  width="20"
                  height="20"
                  class="ms-2"
                  src="../../assets/warning.png"
                  data-bs-toggle="tooltip"
                  :title="schedule.remarks"
                  style="cursor: pointer; position: relative; z-index: 2000;"
                >
              </td>
              <td class="bg-tb-side-header sticky-left-second drag-handle">{{ formatCurrency(schedule.work_unit_price) }}</td>
              <td class="bg-tb-side-header sticky-left-third drag-handle">
                <ul class="mb-0 ps-3" v-if="schedule.is_partner_company">
                  <li v-for="vendor in schedule?.t_building_work_schedule_vendors" :key="vendor.id">
                    {{ vendor?.m_vendor?.vendor_nm }}
                  </li>
                </ul>
              </td>
              <td class="bg-tb-side-header text-nowrap sticky-left-forth drag-handle">
                <router-link
                  :to="{ name: 'AnnualWorkScheduleEdit', params: { id: schedule.id, building_id: buildingId, start_month: startMonth }, query: route.query }"
                >
                  <button type="button" class="btn btn-warning btn-sm mx-1">編集</button>
                </router-link>
                <button
                  class="btn btn-danger btn-sm"
                  :disabled="!canDeleteSchedule(schedule)"
                  data-bs-toggle="modal"
                  data-bs-target="#deleteModal"
                  @click="handleDeleteClick(schedule.id)"
                >
                  削除
                </button>
              </td>
              
              <td
                class="text-nowrap text-end"
                :class="{
                  'bg-light-yellow': getScheduleDetail(schedule, monthOffset)?.is_planned,
                  'text-danger': getScheduleDetail(schedule, monthOffset)?.bill_amount < 0,
                  'bg-light-gray': isBeforeContractEndByScheduleDetail(schedule, monthOffset),
                  'schedule-change-target': canOpenScheduleChangeModal(schedule, monthOffset),
                  'schedule-change-disabled': !canOpenScheduleChangeModal(schedule, monthOffset),
                  'schedule-cell-shift-destination': isShiftedDestination(schedule, monthOffset),
                  'schedule-cell-shift-source': isShiftedSource(schedule, monthOffset),
                  'schedule-cell-cancel': isCanceledCell(schedule, monthOffset)
                }"
                v-for="monthOffset in 12"
                :key="monthOffset"
                @click="handleScheduleCellClick(schedule, monthOffset)"
              >
                {{ formatScheduleDetailAmount(schedule, monthOffset) }}
                <span
                  v-if="shouldShowShiftAlert(schedule, monthOffset)"
                  class="schedule-shift-alert ms-1"
                  data-bs-toggle="tooltip"
                  :title="getScheduleChangeTooltip(schedule, monthOffset)"
                >
                  !
                </span>
                <span
                  v-if="isShiftedSource(schedule, monthOffset)"
                  class="badge text-bg-secondary ms-1"
                  data-bs-toggle="tooltip"
                  :title="getScheduleChangeTooltip(schedule, monthOffset)"
                >
                  変更前
                </span>
                <span
                  v-if="isCanceledCell(schedule, monthOffset)"
                  class="badge text-bg-danger ms-1"
                  data-bs-toggle="tooltip"
                  :title="getScheduleChangeTooltip(schedule, monthOffset)"
                >
                  中止
                </span>
                <!-- 備考アイコン -->
                <img
                  v-if="getScheduleDetailRemarks(schedule, monthOffset)"
                  width="20"
                  height="20"
                  class="ms-2"
                  src="../../assets/warning.png"
                  data-bs-toggle="tooltip"
                  :title="getScheduleDetailRemarks(schedule, monthOffset)"
                  style="cursor: pointer; position: relative; z-index: 50;"
                >
              </td>
              <td class="text-nowrap text-end">
                {{ calculateTotal(schedule.t_building_work_schedule_details) }}
              </td>
            </tr>
          </template>
        </Draggable>

        <!-- 固定行（定額請求や内訳など） -->
        <tr class="month-breakdown-area no-drag-drop-element">
          <td colspan="4" class="text-nowrap bg-tb-side-header sticky-left">月別請求内訳</td>
          <td
            class="text-nowrap text-end"
            v-for="(total, index) in monthlyBillTotals"
            :key="index"
            :class="{
              'bg-light-gray': isBeforeContractEnd(baseDates[index])
            }"
          >
            {{ formatCurrencyZero(total) }}
          </td>
          <td class="text-nowrap text-end">{{ formatCurrencyZero(yearlyTotal) }}</td>
        </tr>

        <tr class="month-tax-area no-drag-drop-element">
          <td colspan="4" class="text-nowrap bg-tb-side-header sticky-left">消費税</td>
          <td
            class="text-nowrap text-end"
            v-for="(tax, index) in monthlyTaxes"
            :key="index"
            :class="{
              'bg-light-gray': isBeforeContractEnd(baseDates[index])
            }"
          >
            {{ formatCurrencyZero(tax) }}
          </td>
          <td class="text-nowrap text-end">{{ formatCurrencyZero(totalTax) }}</td>
        </tr>

        <tr class="monthly-total-area no-drag-drop-element">
          <td colspan="4" class="text-nowrap bg-tb-side-header sticky-left">月額請求合計金額</td>
          <td
            class="text-nowrap text-end"
            v-for="(grandTotal, index) in monthlyGrandTotals"
            :key="index"
            :class="{
              'bg-light-gray': isBeforeContractEnd(baseDates[index])
            }"
          >
            {{ formatCurrencyZero(grandTotal) }}
          </td>
          <td class="text-nowrap text-end">{{ formatCurrencyZero(grandYearlyTotal) }}</td>
        </tr>
      </table>
    </div>

    <!-- 削除確認モーダル -->
    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

    <!-- スケジュール変更モーダル -->
    <ScheduleChangeModal ref="scheduleChangeModal" @onClose="handleScheduleChangeSaved" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { ref, onMounted, computed, nextTick, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import {
  getBuildingAnnualSchedules,
  getBuilding,
  updateScheduleSortOrder,
  deleteBuildingAnnualSchedule
} from '@/api/axios';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import ScheduleChangeModal from './ScheduleChangeModal.vue'; // 同じ階層のモーダルをインポート
import * as bootstrap from 'bootstrap';

export default {
  components: {
    DeleteConfirmationModal,
    ScheduleChangeModal,
    Draggable: draggable
  },
  setup() {
    const title = ref('建物別年間作業予定情報一覧');

    watchEffect(() => {
      document.title = title.value;
      nextTick(() => {
        initTooltips();
      });
    });

    const route = useRoute();
    const store = useStore();
    const userRole = computed(() => store.state.userRole ?? localStorage.getItem('userRole'));
    const isAdmin = computed(() => ['0', '999', 0, 999].includes(userRole.value));
    const year = ref(new Date(route.params.start_month).getFullYear());
    const month = ref(new Date(route.params.start_month).getMonth() + 1);
    const startMonth = ref(new Date(route.params.start_month));
    const baseDates = computed(() =>
      Array.from({ length: 12 }, (_, i) => new Date(year.value, month.value - 1 + i))
    );
    const buildingId = ref(route.params.building_id);
    const schedules = ref([]);
    const building = ref({});
    const scheduleChangeModal = ref(null); // モーダル用のref
    const paramsIdToDelete = ref(null); // 削除モーダルへ渡すID
    const isSortUpdated = ref(false); // 並べ替えのフラグ

    const extractErrorMessage = (error, fallbackMessage) => {
      return error?.response?.data?.error || error?.response?.data?.errors?.[0] || fallbackMessage;
    };


    // Fetch schedules from API
    const fetchSchedules = async () => {
      try {
        const response = await getBuildingAnnualSchedules(buildingId.value, year.value, month.value);
        schedules.value = Array.isArray(response.data.annual_schedules)
          ? response.data.annual_schedules
          : [];
      } catch (error) {
        console.error('年間スケジュールの取得に失敗しました:', error);
      }

      // 建物情報
      try {
        const response = await getBuilding(buildingId.value);
        building.value = response.data;
      } catch (error) {
        console.error('建物情報ーデータの取得に失敗しました:', error);
      }
    };

    // Helper to display the month name for each offset
    const displayMonth = (monthOffset) => {
      const date = new Date(startMonth.value);
      date.setMonth(startMonth.value.getMonth() + monthOffset - 1);
      return `${date.getFullYear()}年${date.getMonth() + 1}月`;
    };

    // Helper to get month in 'YYYY-MM' format for routing
    const getMonth = (monthOffset) => {
      const date = new Date(startMonth.value);
      date.setMonth(startMonth.value.getMonth() + monthOffset - 1);
      return date.getMonth() + 1;
    };

    const isBeforeContractEnd = (date) => {
      // 契約終了日が存在しない → すべて「契約終了前扱いではない」
      if (!building.value?.contract_end_dt) return false;

      const endDate = new Date(building.value.contract_end_dt);

      return (
        date.getFullYear() > endDate.getFullYear() ||
        (date.getFullYear() === endDate.getFullYear() &&
        date.getMonth() + 1 > endDate.getMonth() + 1)
      );
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '';
    };

    // Format currency
    const formatCurrencyZero = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    // 合計を計算するメソッド
    const calculateTotal = (details) => {
      if (!details) return '¥0';

      // 契約終了日が存在しなければ、すべての明細を加算する
      if (!building.value?.contract_end_dt) {
        const total = details.reduce((sum, detail) => {
          return sum + (detail?.bill_amount || 0);
        }, 0);
        return formatCurrencyZero(total);
      }

      // 契約終了日がある場合のみ比較ロジックを使用
      const endDate = new Date(building.value.contract_end_dt);
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;

      const total = details.reduce((sum, detail) => {
        if (!detail) return sum;

        // 契約終了年月「より後」のデータは加算しない
        if (
          detail.work_year > endYear ||
          (detail.work_year === endYear && detail.work_month > endMonth)
        ) {
          return sum;
        }

        return sum + (detail?.bill_amount || 0);
      }, 0);

      return formatCurrencyZero(total);
    };

    const getScheduleDetail = (schedule, monthOffset) => {
      return schedule?.t_building_work_schedule_details?.[monthOffset - 1] || null;
    };

    const getScheduleDetailChangeDisplayType = (schedule, monthOffset) => {
      return getScheduleDetail(schedule, monthOffset)?.change_display_type || null;
    };

    const isShiftedDestination = (schedule, monthOffset) => {
      return getScheduleDetailChangeDisplayType(schedule, monthOffset) === 'shifted_destination';
    };

    const isShiftedSource = (schedule, monthOffset) => {
      return getScheduleDetailChangeDisplayType(schedule, monthOffset) === 'shifted_source';
    };

    const isCanceledCell = (schedule, monthOffset) => {
      return getScheduleDetailChangeDisplayType(schedule, monthOffset) === 'cancel';
    };

    const isBeforeContractEndByScheduleDetail = (schedule, monthOffset) => {
      const detail = getScheduleDetail(schedule, monthOffset);
      if (!detail) return false;

      const detailDate = new Date(detail.work_year, detail.work_month - 1);
      return isBeforeContractEnd(detailDate);
    };

    const formatScheduleDetailAmount = (schedule, monthOffset) => {
      const detail = getScheduleDetail(schedule, monthOffset);
      if (!detail) return '';

      if (isShiftedSource(schedule, monthOffset) || isCanceledCell(schedule, monthOffset)) {
        return '';
      }

      const detailDate = new Date(detail.work_year, detail.work_month - 1);
      if (isBeforeContractEnd(detailDate)) {
        return '¥0';
      }

      return schedule.t_building_work_annual_schedule_id
        ? formatCurrencyZero(detail.bill_amount) || ''
        : formatCurrency(detail.bill_amount) || '';
    };

    const getScheduleDetailRemarks = (schedule, monthOffset) => {
      const detail = getScheduleDetail(schedule, monthOffset);
      return detail?.remarks || '';
    };

    const getScheduleChangeTooltip = (schedule, monthOffset) => {
      const detail = getScheduleDetail(schedule, monthOffset);
      if (!detail?.change_reason) {
        if (isShiftedDestination(schedule, monthOffset)) return '予定変更後の作業月です';
        if (isShiftedSource(schedule, monthOffset)) return 'この月の作業予定は別月へ移動しました';
        if (isCanceledCell(schedule, monthOffset)) return 'この月の作業予定は中止されました';
        return '';
      }

      if (isShiftedDestination(schedule, monthOffset)) return `予定変更理由: ${detail.change_reason}`;
      if (isShiftedSource(schedule, monthOffset)) return `移動理由: ${detail.change_reason}`;
      if (isCanceledCell(schedule, monthOffset)) return `中止理由: ${detail.change_reason}`;
      return detail.change_reason;
    };

    const shouldShowShiftAlert = (schedule, monthOffset) => {
      return isShiftedDestination(schedule, monthOffset);
    };

    const isBlankScheduleCell = (schedule, monthOffset) => {
      const detail = getScheduleDetail(schedule, monthOffset);
      if (!detail?.id) return true;

      const hasVisibleAmount = formatScheduleDetailAmount(schedule, monthOffset) !== '';
      const hasVisibleStatusBadge =
        shouldShowShiftAlert(schedule, monthOffset) ||
        isShiftedSource(schedule, monthOffset) ||
        isCanceledCell(schedule, monthOffset);
      const hasVisibleRemark = getScheduleDetailRemarks(schedule, monthOffset) !== '';

      return !hasVisibleAmount && !hasVisibleStatusBadge && !hasVisibleRemark;
    };

    // Compute monthly bill totals
    const monthlyBillTotals = computed(() => {
      const scheduleList = Array.isArray(schedules.value) ? schedules.value : [];
      if (!scheduleList.length) return Array(12).fill(0);

      const allDetails = scheduleList.flatMap(
        schedule => schedule.t_building_work_schedule_details || []
      ).filter(detail => detail?.bill_amount);

      if (allDetails.length === 0) return Array(12).fill(0);

      allDetails.sort((a, b) => {
        const dateA = a.work_year * 100 + a.work_month;
        const dateB = b.work_year * 100 + b.work_month;
        return dateA - dateB;
      });

      const baseYear = year.value;
      const baseMonth = month.value;

      const endDt = building.value?.contract_end_dt ? new Date(building.value.contract_end_dt) : null;
      const endYear = endDt ? endDt.getFullYear() : null;
      const endMonth = endDt ? endDt.getMonth() + 1 : null;

      const totals = Array(12).fill(0);

      allDetails.forEach(detail => {

        // ★ 契約終了日が空の場合は除外しない
        if (endDt) {
          // 契約終了以降ならスキップ
          if (
            detail.work_year > endYear ||
            (detail.work_year === endYear && detail.work_month > endMonth)
          ) {
            return;
          }
        }

        const index = (detail.work_year - baseYear) * 12 + (detail.work_month - baseMonth);

        if (index >= 0 && index < 12) {
          totals[index] += detail.bill_amount || 0;
        }
      });


      // ▼▼▼ 定額請求モード ▼▼▼
      if (building.value.fixed_fee_billing) {
        const annualTotal = totals.reduce((sum, val) => sum + val, 0);

        const targetMonths = [];

        for (let i = 0; i < 12; i++) {
          const currentDate = new Date(baseYear, baseMonth - 1 + i);
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth() + 1;

          if (endDt) {
            // 契約終了後なら対象外
            if (
              currentYear > endYear ||
              (currentYear === endYear && currentMonth > endMonth)
            ) {
              continue;
            }
          }

          targetMonths.push({ index: i, year: currentYear, month: currentMonth });
        }

        const validMonthCount = targetMonths.length;

        if (validMonthCount === 0) return Array(12).fill(0);

        const fixedAmount = Math.floor(annualTotal / validMonthCount);
        const remainder = annualTotal - fixedAmount * validMonthCount;

        const fixedTotals = Array(12).fill(0);

        targetMonths.forEach(({ index }) => {
          fixedTotals[index] = fixedAmount;
        });

        fixedTotals[targetMonths.at(-1).index] += remainder;

        return fixedTotals;
      }

      return totals;
    });

    const annualTotalAmount = computed(() => {
      return monthlyBillTotals.value.reduce((sum, val) => sum + val, 0);
    });

    // Compute taxes for each month (10% of monthly totals)
    const monthlyTaxes = computed(() => {
      return monthlyBillTotals.value.map(total => total * 0.1);
    });

    // Compute monthly grand totals (bill amount + tax)
    const monthlyGrandTotals = computed(() => {
      return monthlyBillTotals.value.map((total) => total + (total * 0.1));
    });

    // Calculate yearly totals
    const yearlyTotal = computed(() => monthlyBillTotals.value.reduce((sum, total) => sum + total, 0));
    const totalTax = computed(() => monthlyTaxes.value.reduce((sum, tax) => sum + tax, 0));
    const grandYearlyTotal = computed(() => yearlyTotal.value + totalTax.value);

    const canDeleteSchedule = (schedule) => {
      return isAdmin.value || !schedule?.has_billed_details;
    };

    const isScheduleDetailLocked = (detail) => {
      return !isAdmin.value && !!detail?.locked_for_general_user;
    };

    const getScheduleDetailLockMessage = (detail) => {
      if (detail?.has_billing) {
        return '請求済みの月は編集できません';
      }

      if (detail?.locked_for_general_user) {
        return '先月より前の月は編集できません';
      }

      return 'この月は編集できません';
    };

    // 削除
    const handleDeleteClick = (scheduleId) => {
      const schedule = schedules.value.find(item => item.id === scheduleId);
      if (!canDeleteSchedule(schedule)) {
        store.dispatch('setFlashMessage', {
          message: '請求書作成済みの年間作業予定は管理者以外編集できません',
          type: 'danger'
        });
        return;
      }
      paramsIdToDelete.value = scheduleId;
    };

    const handleConfirmDelete = async (scheduleId) => {
      try {
        await deleteBuildingAnnualSchedule(scheduleId);
        // リストから測定情報ーを削除
        schedules.value = schedules.value.filter(schedule => schedule.id !== scheduleId);
        store.dispatch('setFlashMessage', { message: '年間予定作業情報が削除されました', type: 'success' });
        await fetchSchedules();
        await nextTick();
        initTooltips();
      } catch (error) {
        // エラー処理
        const message = extractErrorMessage(error, '年間予定作業情報削除に失敗しました');
        store.dispatch('setFlashMessage', { message, type: 'danger' });
      }
    };

    // 年度別の取得関数
    const prevFiscal = (date) => getFiscalYearRange(date, -1);
    const nextFiscal = (date) => getFiscalYearRange(date, 1);
    const prevFiscalValue = computed(() => prevFiscal(startMonth.value))
    const nextFiscalValue = computed(() => nextFiscal(startMonth.value))


    // 汎用の年度範囲関数（共通ロジック）
    const getFiscalYearRange = (contractDate, offset = 0) => {
      const baseDate = new Date(contractDate) // パラメータから渡された日付
      const contractMonth = baseDate.getMonth() + 1
      const baseYear = baseDate.getFullYear()

      const targetYear = baseYear + offset
      const startMonth = new Date(targetYear, contractMonth - 1)
      const endMonth = new Date(targetYear + 1, contractMonth - 2)

      return {
        start: `${startMonth.getFullYear()}-${startMonth.getMonth() + 1}-1`, // 1日は固定でOK
        end: endMonth,
        label: `${startMonth.getFullYear()}年${startMonth.getMonth() + 1}月〜${endMonth.getFullYear()}年${endMonth.getMonth() + 1}月`
      }
    }

    const canOpenScheduleChangeModal = (schedule, monthOffset) => {
      const detail = getScheduleDetail(schedule, monthOffset);
      if (!detail?.id) return false;

      return !isBlankScheduleCell(schedule, monthOffset) && !isScheduleDetailLocked(detail);
    };

    const openScheduleChangeModal = ({ scheduleDetailId, targetWorkYear, targetWorkMonth }) => {
      if (scheduleChangeModal.value) {
        const targetSchedule = schedules.value.find(schedule =>
          (schedule.t_building_work_schedule_details || []).some(detail => detail?.id === scheduleDetailId)
        );

        scheduleChangeModal.value.show({
          workScheduleDetailId: scheduleDetailId,
          targetWorkYear,
          targetWorkMonth,
          scheduleDetails: targetSchedule?.t_building_work_schedule_details || []
        });
      }
    };

    const handleScheduleCellClick = (schedule, monthOffset) => {
      const detail = getScheduleDetail(schedule, monthOffset);
      if (!detail?.id) return;
      if (!canOpenScheduleChangeModal(schedule, monthOffset)) {
        if (isScheduleDetailLocked(detail)) {
          store.dispatch('setFlashMessage', {
            message: getScheduleDetailLockMessage(detail),
            type: 'danger'
          });
        }
        return;
      }

      openScheduleChangeModal({
        scheduleDetailId: detail.id,
        // 画面でクリックした“そのセルの年月”を対象にする。
        // 移動後セルで再変更する時に、初回変更元年月へ巻き戻らないようにする。
        targetWorkYear: detail.work_year,
        targetWorkMonth: detail.work_month
      });
    };

    const handleScheduleChangeSaved = async () => {
      await fetchSchedules();
      await nextTick();
      initTooltips();
    };

    // ツールチップ初期化用関数
    const initTooltips = () => {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(el => {
        if (!el._tooltipInstance) {
          el._tooltipInstance = new bootstrap.Tooltip(el, {
            container: 'body',
            boundary: 'window',
            trigger: 'hover focus'
          });
        }
      });
    };

    // 並び替え後に sort_no を再付番する関数
    const onReorderEnd = () => {
      schedules.value.forEach((schedule, index) => {
        schedule.sort_no = index + 1; // 上から順に sort_no を振り直す
      });
      isSortUpdated.value = true; // 並び替え変更フラグON
    };

    // 並べ替え処理
    const saveSortOrder = async () => {
      try {
        const payload = schedules.value.map(schedule => ({
          id: schedule.id,
          sort_no: schedule.sort_no
        }));
        await updateScheduleSortOrder(payload);
        isSortUpdated.value = false;
        store.dispatch('setFlashMessage', { message: '並び順を保存しました', type: 'success' });
      } catch (error) {
        const message = extractErrorMessage(error, '並び順の保存に失敗しました');
        store.dispatch('setFlashMessage', { message, type: 'danger' });
        console.error(error);
      }
    };

    onMounted(async () => {
      await fetchSchedules();
      await nextTick();
      initTooltips();
    });

    // パラメータ変更に応答して再フェッチ
    watch(
      () => [route.params.start_month, route.params.building_id],
      ([newStart, newBuildingId]) => {
        if (newStart) {
          const d = new Date(newStart)
          startMonth.value = d
          year.value = d.getFullYear()
          month.value = d.getMonth() + 1
        }

        if (newBuildingId) {
          buildingId.value = newBuildingId
        }

        // 再ロードではなく、データを差し替える
        fetchSchedules()
          .then(() => nextTick())
          .then(initTooltips)
      }
    );

    return { 
      schedules, 
      building,
      year, 
      baseDates,
      buildingId, 
      displayMonth,
      getMonth,
      isBeforeContractEnd,
      formatCurrency,
      formatCurrencyZero,
      calculateTotal,
      getScheduleDetail,
      getScheduleDetailChangeDisplayType,
      isShiftedDestination,
      isShiftedSource,
      isCanceledCell,
      isBeforeContractEndByScheduleDetail,
      formatScheduleDetailAmount,
      getScheduleDetailRemarks,
      getScheduleChangeTooltip,
      shouldShowShiftAlert,
      monthlyBillTotals,
      annualTotalAmount,
      monthlyTaxes, 
      monthlyGrandTotals, 
      yearlyTotal, 
      totalTax, 
      grandYearlyTotal,
      paramsIdToDelete,
      canDeleteSchedule,
      handleDeleteClick,
      handleConfirmDelete,
      prevFiscal,
      nextFiscal,
      prevFiscalValue,
      nextFiscalValue,
      scheduleChangeModal,
      canOpenScheduleChangeModal,
      openScheduleChangeModal,
      handleScheduleCellClick,
      handleScheduleChangeSaved,
      isSortUpdated,
      onReorderEnd,
      saveSortOrder,
      route
    };
  }
};
</script>

<style scoped>
.warning-comment{
  font-size: 14px;
  font-weight: bold;
}
.warning-comment p span{
  color: #e5ffbf;
}

/* テーブルラッパーのスタイル */
.table-wrapper {
  max-height: 600px; /* テーブルの高さを指定して、縦スクロールを発生させる */
  overflow-y: auto;  /* 縦スクロールを許可 */
  background: #BBBBBB;
  border: 1px solid #BBBBBB;
}

.table-wrapper table{
  background: #FFFFFF;
  border: 1px solid #BBBBBB;
}

.table-wrapper thead th {
  position: sticky;
  top: 0;
  background: #003366 !important; /* 背景色を設定し、スクロール中に背景が透けるのを防ぐ */
  color: #fff !important;
  z-index: 98;
}
.table-wrapper thead th:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-left: 1px solid #BBBBBB;
  z-index: 100;
}

.table-wrapper .th-sticky{
  z-index: 99 !important;
}

/* 横スクロール時に固定するカラム */
.table-wrapper .sticky-left,
.table-wrapper .sticky-left-second,
.table-wrapper .sticky-left-third,
.table-wrapper .sticky-left-forth{
  position: sticky;
  z-index: 90;
}
.table-wrapper .sticky-left:before,
.table-wrapper .sticky-left-second:before,
.table-wrapper .sticky-left-second:after,
.table-wrapper .sticky-left-third:before,
.table-wrapper .sticky-left-forth:before,
.table-wrapper .sticky-left-forth:after{
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  border-left: 1px solid #BBBBBB;
  z-index: 99;
}
.table-wrapper .sticky-left:before,
.table-wrapper .sticky-left-second:before,
.table-wrapper .sticky-left-second:after,
.table-wrapper .sticky-left-third:before{ content: ""; }

.table-wrapper .sticky-left{
  left: 0px;
  min-width: 200px;
  width: 200px;
  max-width: 200px;
}
.table-wrapper .sticky-left:before{ left: -1px; }

.table-wrapper .sticky-left-second{
  left: 201px;
  min-width: 150px;
  width: 150px;
  max-width: 150px;
}
.table-wrapper .sticky-left-second:before{ left: -2px; }
.table-wrapper .sticky-left-second:after{ left: 149px; }

.table-wrapper .sticky-left-third{
  left: 351px;
  min-width: 200px;
  width: 200px;
  max-width: 200px;
}
.table-wrapper .sticky-left-third:before{ left: -1px; }
.table-wrapper .sticky-left-third:after{ left: 199px; }

.table-wrapper .sticky-left-forth{
  left: 551px;
  min-width: 120px;
  width: 120px;
  max-width: 120px;
}
.table-wrapper .sticky-left-forth:before{ left: -1px; }
.table-wrapper .sticky-left-forth:after{ left: 119px; }

table th,
table td{
  border: 1px solid #BBBBBB;
  font-size: 12px;
}

table th.bg-tb-header{
  background-color: #003366;
  color: #fff;
}

table .schedule-data-area td.bg-tb-side-header{
  background-color: #f0f8ff;
}

table .month-breakdown-area td.bg-tb-side-header,
table .month-tax-area td.bg-tb-side-header{
  background-color: #fafad2;
}

table .month-breakdown-area{
  border-top: 3px dotted #BBBBBB;
}

table .month-tax-area{
  border-bottom: 3px dotted #BBBBBB;
}

table .monthly-total-area td.bg-tb-side-header{
  background-color: #f0e68c;
}

table .no-drag-drop-element th,
table .no-drag-drop-element td{
  padding: 0.5rem;
}

/* .schedule-change-btn{
  z-index: 1000;
} */

.bg-color-fixed-fee{
  background-color: #d7c7fe;
}
.bg-light-yellow {
  background-color: #e5ffbf; /* 薄い黄色（レモン系） */
}
.bg-light-gray {
  background-color: #807e7e; /* 薄い黄色（レモン系） */
  color: #fff;
}

.drag-handle {
  cursor: grab;
}

.schedule-change-target {
  cursor: pointer;
}

.schedule-change-disabled {
  cursor: not-allowed;
  opacity: 0.85;
}

.schedule-change-target:hover {
  filter: brightness(0.97);
}

.schedule-cell-shift-destination {
  box-shadow: inset 0 0 0 2px #f5a623;
}

.schedule-cell-shift-source {
  box-shadow: inset 0 0 0 2px #90a4ae;
}

.schedule-cell-cancel {
  box-shadow: inset 0 0 0 2px #e57373;
}

.schedule-shift-alert {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #f5a623;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  cursor: help;
}
</style>
